import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useGuardRoleContext } from "./GuardRoleProvider";
import { getScheduledItems } from "api/canguard/getScheduledItems";
import { Swiper as SwiperType } from "swiper";
import { convertToScheduleAssignment } from "../utils/convertToScheduleAssignment";
import { enhancePatrolData } from "../utils/enhancePatrolData";
import { useNotificationProvider } from "hooks";

const defaultScheduleContextValue: {
  schedule: ScheduleAssignment[];
  setSchedule: React.Dispatch<React.SetStateAction<ScheduleAssignment[]>>;
  isLoadingSchedule: boolean;
  swiperRef?: React.MutableRefObject<SwiperType | null>;
  goToPatrol: (index: number) => void;
  updatePatrolInSchedule: (updatedPatrol: ScheduleAssignment) => void;
  fetchSchedule: () => void;
  currentViewedPatrolIndex: number;
  setCurrentViewedPatrolIndex: React.Dispatch<React.SetStateAction<number>>;
  currentViewedPatrol: ScheduleAssignment | null;
  // V4 selection state
  selectedPatrolId: number | null;
  setSelectedPatrolId: React.Dispatch<React.SetStateAction<number | null>>;
} = {
  schedule: [],
  setSchedule: () => {},
  isLoadingSchedule: false,
  swiperRef: undefined,
  goToPatrol: () => {},
  updatePatrolInSchedule: () => {},
  fetchSchedule: () => {},
  currentViewedPatrolIndex: 0,
  setCurrentViewedPatrolIndex: () => {},
  currentViewedPatrol: null,
  // V4 selection state
  selectedPatrolId: null,
  setSelectedPatrolId: () => {},
};

const ScheduleContext = createContext(defaultScheduleContextValue);

export const useScheduleContext = () => useContext(ScheduleContext);

interface ScheduleProviderProps {
  children: React.ReactNode;
}

export const ScheduleProvider = ({ children }: ScheduleProviderProps) => {
  const [schedule, setSchedule] = useState<ScheduleAssignment[]>([]);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState<boolean>(false);
  const [currentViewedPatrolIndex, setCurrentViewedPatrolIndex] = useState<number>(0);
  // V4: Selection state for hybrid list+detail layout
  const [selectedPatrolId, setSelectedPatrolId] = useState<number | null>(null);
  const { isAdmin, guardSelectorOptions } = useGuardRoleContext();
  const swiperRef = useRef<SwiperType | null>(null);
  const { notifyError } = useNotificationProvider();

  // Compute the currently viewed patrol from schedule and index
  const currentViewedPatrol = schedule.length > 0 ? schedule[currentViewedPatrolIndex] || null : null;
  const goToPatrol = (index: number) => {
    if (swiperRef.current && schedule && schedule.length > 0) {
      swiperRef?.current.slideToLoop(index);
    }
  };

  const updatePatrolInSchedule = (updatedPatrol: ScheduleAssignment) => {
    const updatedSchedule = schedule.map((patrol) =>
      patrol.schedule_assignment_id === updatedPatrol.schedule_assignment_id
        ? { ...updatedPatrol } // ✅ force new object reference
        : patrol
    );
    setSchedule(updatedSchedule);
  };

  const fetchSchedule = async () => {
    try {
      setIsLoadingSchedule(true);
      const response = await getScheduledItems(
        guardSelectorOptions.selectedGuardID,
        guardSelectorOptions.selectedDate
      );
      if (Array.isArray(response)) {
        const convertedSchedule: ScheduleAssignment[] = response.map(
          (item: any) => convertToScheduleAssignment(item)
        );
        const enhancedSchedule: ScheduleAssignment[] =
          enhancePatrolData(convertedSchedule);
        setSchedule(enhancedSchedule);
      } else {
        console.error("[ScheduleProvider/fetchSchedule] Expected an array but got:", response);
      }
    } catch (error) {
      console.error("[ScheduleProvider/fetchSchedule] Error fetching schedule:", error);
      notifyError("Failed to fetch schedule");
    } finally {
      setIsLoadingSchedule(false);
    }
  };

  // TODO: shouldn't fetch schedule on initial render is admin is true. on the initial load, it fetched the schedule even when the user is not an admin. This is because by default, isAdmin is false and the useEffect will run
  useEffect(() => {
    if (!isAdmin) {
      fetchSchedule();
    } else if (
      guardSelectorOptions.selectedGuardID &&
      guardSelectorOptions.selectedDate
    ) {
      fetchSchedule();
    }
  }, [isAdmin, guardSelectorOptions]);

  // V4: Auto-select first patrol when schedule loads or changes
  useEffect(() => {
    if (schedule.length > 0 && selectedPatrolId === null) {
      // Prefer the first in-progress patrol, otherwise first patrol
      const inProgressPatrol = schedule.find((p) => p.started && !p.complete);
      const patrolToSelect = inProgressPatrol || schedule[0];
      setSelectedPatrolId(patrolToSelect.schedule_assignment_id);
    } else if (schedule.length === 0 && selectedPatrolId !== null) {
      // Clear selection when schedule is empty
      setSelectedPatrolId(null);
    }
  }, [schedule]);

  const value = {
    schedule,
    setSchedule,
    isLoadingSchedule,
    swiperRef,
    goToPatrol,
    updatePatrolInSchedule,
    fetchSchedule,
    currentViewedPatrolIndex,
    setCurrentViewedPatrolIndex,
    currentViewedPatrol,
    // V4 selection state
    selectedPatrolId,
    setSelectedPatrolId,
  };
  return (
    <ScheduleContext.Provider value={value}>
      {children}
    </ScheduleContext.Provider>
  );
};
